import { useCallback } from "react";
import { FiChevronRight, FiZoomIn, FiZoomOut } from "react-icons/fi";

interface PdfPreviewZoomBarProps {
    currentPreviewWidth: number;
    previewDefaultWidth: number;
    previewMaxWidth: number;
    previewMinWidth: number;
    showSampleDataBanner?: boolean;
    totalPages: number;
    setPreviewWidth: (width: number) => void;
    onToggleCollapse?: () => void;
}

const ZOOM_STEP_PERCENT = 10;

export const PdfPreviewZoomBar = (
    { currentPreviewWidth, previewDefaultWidth, previewMaxWidth, previewMinWidth, showSampleDataBanner = false, totalPages, onToggleCollapse, setPreviewWidth }: PdfPreviewZoomBarProps) => {

    const zoomPercent = Math.round((currentPreviewWidth / previewDefaultWidth) * 100);
    const zoomStep = previewDefaultWidth * (ZOOM_STEP_PERCENT / 100);

    const handleZoomIn = useCallback(() => {
        setPreviewWidth(Math.min(currentPreviewWidth + zoomStep, previewMaxWidth));
    }, [currentPreviewWidth, zoomStep, previewMaxWidth, setPreviewWidth]);

    const handleZoomOut = useCallback(() => {
        setPreviewWidth(Math.max(currentPreviewWidth - zoomStep, previewMinWidth));
    }, [currentPreviewWidth, zoomStep, previewMinWidth, setPreviewWidth]);

    return <div className="sticky top-0 z-10 flex items-center justify-between bg-gray-50 p-4 w-full">
        {showSampleDataBanner && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-1.5 rounded mb-3 font-medium text-center">
            SAMPLE DATA — PREVIEW ONLY
        </div>}
        <span className="text-sm font-medium text-gray-700">
            Live Preview ({totalPages} {totalPages === 1 ? "page" : "pages"})
        </span>
        <div className="flex items-center gap-1">
            <button
                type="button"
                onClick={handleZoomOut}
                disabled={currentPreviewWidth <= previewMinWidth}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Zoom out"
            >
                <FiZoomOut className="h-6 w-6" />
            </button>
            <span className="text-md text-gray-500 w-10 text-center">{zoomPercent}%</span>
            <button
                type="button"
                onClick={handleZoomIn}
                disabled={currentPreviewWidth >= previewMaxWidth}
                className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Zoom in"
            >
                <FiZoomIn className="h-6 w-6" />
            </button>
            {onToggleCollapse && (
                <button
                    type="button"
                    onClick={onToggleCollapse}
                    className="p-1 rounded hover:bg-gray-200 ml-1"
                    aria-label="Collapse preview"
                >
                    <FiChevronRight className="h-6 w-6" />
                </button>
            )}
        </div>
    </div>
}